/**
 * Fleet (day) report menu gates.
 *
 * RACE SUMMARY is shown only when the day has a numbered RACE event. Practice
 * prestarts carry a Race_number and are not races.
 *
 * CALIBRATION is shown only when a correction was fit. Pass-through still
 * publishes Tws_cor and Awa_offset_deg = 0; those do not count. Keep this
 * channel set aligned with server_python/scripts/gp50/helpers/day_report_gates.py.
 */

const APPLIED_CORRECTION_CHANNELS: ReadonlySet<string> = new Set([
  "twd_shear_deg",
  "cur_rate_est_kph",
  "cur_dir_est_deg",
  "cur_confidence",
  "lwy_cur_deg",
]);

export interface FleetDayReportGates {
  date: string;
  hasRaces: boolean;
  hasAppliedCorrections: boolean;
}

type GetJson = (url: string, signal?: AbortSignal) => Promise<{ success?: boolean; data?: unknown } | null | undefined>;

export function channelsHaveAppliedCorrections(channels: readonly unknown[] | null | undefined): boolean {
  if (!channels) return false;
  for (const name of channels) {
    const key = String(name ?? "").trim().toLowerCase();
    if (!key) continue;
    if (APPLIED_CORRECTION_CHANNELS.has(key) || key.startsWith("awa_offset__")) return true;
  }
  return false;
}

export function raceNumberFromTags(tags: unknown): number {
  let parsed: unknown = tags;
  if (typeof parsed === "string") {
    const text = parsed.trim();
    if (!text) return 0;
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      return 0;
    }
  }
  if (!parsed || typeof parsed !== "object") return 0;
  const record = parsed as Record<string, unknown>;
  let raw: unknown = record.Race_number;
  if ((raw == null || raw === "") && record.RACES && typeof record.RACES === "object") {
    raw = (record.RACES as Record<string, unknown>).Race_number;
  }
  if (raw == null || raw === "") return 0;
  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n)) return 0;
  return Math.trunc(n);
}

export function rowsHaveNumberedRaces(rows: readonly unknown[] | null | undefined): boolean {
  if (!rows) return false;
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    if (raceNumberFromTags((row as { tags?: unknown }).tags) > 0) return true;
  }
  return false;
}

/** Pending gates (null) hide race and calibration until the day has been checked. */
export function fleetDayReportIsVisible(
  pageName: string | null | undefined,
  gates: Pick<FleetDayReportGates, "hasRaces" | "hasAppliedCorrections"> | null,
): boolean {
  const key = String(pageName || "").replace(/\s+/g, "").toUpperCase();
  if (key !== "RACESUMMARY" && key !== "CALIBRATION") return true;
  if (!gates) return false;
  if (key === "RACESUMMARY") return gates.hasRaces;
  return gates.hasAppliedCorrections;
}

export interface FleetReportApiRoots {
  datasets: string;
  events: string;
}

/**
 * Day-level gates for the fleet report menu.
 * A failed dataset-list or channel read fails open (keep the report) so a
 * transport error does not hide a real race or calibration day.
 */
export async function fetchFleetDayReportGates(
  getData: GetJson,
  api: FleetReportApiRoots,
  className: string,
  projectId: string | number,
  dateNorm: string,
  signal?: AbortSignal,
): Promise<FleetDayReportGates> {
  const enc = encodeURIComponent;
  const listUrl =
    `${api.datasets}/date/dataset_id?class_name=${enc(className)}` +
    `&project_id=${enc(String(projectId))}&date=${enc(dateNorm)}`;
  const channelsUrl =
    `${api.datasets}/channels?class_name=${enc(className)}` +
    `&project_id=${enc(String(projectId))}&date=${enc(dateNorm)}`;

  const [listRes, channelRes] = await Promise.all([
    getData(listUrl, signal),
    getData(channelsUrl, signal),
  ]);

  let hasRaces = true;
  if (listRes?.success !== false) {
    const datasets = Array.isArray(listRes?.data) ? listRes.data : [];
    const ids = datasets
      .map((row) => (row && typeof row === "object" ? (row as { dataset_id?: unknown }).dataset_id : null))
      .filter((id): id is string | number => id != null && String(id) !== "");
    const checks = await Promise.all(ids.map(async (id) => {
      const url =
        `${api.events}/info?class_name=${enc(className)}&project_id=${enc(String(projectId))}` +
        `&dataset_id=${enc(String(id))}&event_type=RACE&timezone=UTC`;
      const res = await getData(url, signal);
      if (res?.success === false) return true;
      const rows = Array.isArray(res?.data) ? res.data : [];
      return rowsHaveNumberedRaces(rows);
    }));
    hasRaces = checks.some(Boolean);
  }

  let hasAppliedCorrections = true;
  if (channelRes?.success !== false) {
    const names = Array.isArray(channelRes?.data) ? channelRes.data : [];
    hasAppliedCorrections = channelsHaveAppliedCorrections(names);
  }

  return { date: dateNorm, hasRaces, hasAppliedCorrections };
}
